import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { supabase } from '../../supabaseClient';
import Dropdown from '../Dropdown';
import { DropdownItem } from '../Dropdown/config';
import Modal from '../Modal';
import SearchableProjectDropdown from '../SearchableProjectDropdown';
import PanelHeading from '../PanelHeading';
import Field from '../Field';
import TextInput from '../TextInput';
import MemberChips from '../MemberChips';
import StageContactField from '../StageContactField';
import { errorMessage } from '../../utils/supabaseErrors';

// =======================================
// INTERFACES
// =======================================

interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
}

interface AddStageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStageCreated: () => void;
  teamMembers: TeamMember[];
  /** Names the modal ("New Design stage"). The project search itself offers every type. */
  workflowType: string;
}

interface FormData {
  parent_project_id: string;
  stage_id: string;
  stage_description: string;
  project_lead: string;
  team_members: string[];
  /** '' inherits the parent project's primary contact. */
  primary_contact: string;
}

interface FormErrors {
  parent_project_id?: string;
  stage_id?: string;
  stage_description?: string;
  general?: string;
}

const EMPTY_FORM: FormData = {
  parent_project_id: '',
  stage_id: '',
  stage_description: '',
  project_lead: '',
  team_members: [],
  primary_contact: ''
};

// =======================================
// MAIN COMPONENT
// =======================================

const AddStageModal: React.FC<AddStageModalProps> = ({
  isOpen,
  onClose,
  onStageCreated,
  teamMembers,
  workflowType
}) => {
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** The chosen parent's job number, for the "will be numbered" preview. */
  const [parentJobNumber, setParentJobNumber] = useState<string | null>(null);
  /** The chosen parent's client: a stage's contact is one of its contacts. */
  const [parentClientId, setParentClientId] = useState<number | null>(null);
  /** The parent's own primary contact, named in the "inherit" entry. */
  const [parentContactName, setParentContactName] = useState<string | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData(EMPTY_FORM);
      setErrors({});
      setParentJobNumber(null);
      setParentClientId(null);
      setParentContactName(null);
    }
  }, [isOpen]);

  // Look up the parent's primary contact to name it in the "inherit" entry.
  useEffect(() => {
    const parentId = formData.parent_project_id ? parseInt(formData.parent_project_id, 10) : null;
    if (!parentId) {
      setParentContactName(null);
      return;
    }
    let cancelled = false;
    supabase
      .from('projects')
      .select('contacts!primary_contact ( first_name, last_name )')
      .eq('id', parentId)
      .single()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('Error fetching parent project contact:', error);
          setParentContactName(null);
          return;
        }
        const contact = data?.contacts as unknown as { first_name: string; last_name: string } | null;
        setParentContactName(contact ? `${contact.first_name} ${contact.last_name}`.trim() : null);
      });
    return () => { cancelled = true; };
  }, [formData.parent_project_id]);

  // Sync project lead with team members - when project lead is selected, add them to team members
  useEffect(() => {
    if (formData.project_lead) {
      setFormData(prev => {
        const alreadyInTeam = prev.team_members.includes(prev.project_lead);
        if (!alreadyInTeam) {
          return {
            ...prev,
            team_members: [...prev.team_members, prev.project_lead]
          };
        }
        return prev;
      });
    }
  }, [formData.project_lead]);

  // Validate form
  const validateForm = useCallback((): FormErrors => {
    const newErrors: FormErrors = {};

    if (!formData.parent_project_id) {
      newErrors.parent_project_id = 'Choose the project this stage belongs to';
    }

    if (!formData.stage_id.trim()) {
      newErrors.stage_id = 'Stage ID is required';
    }

    if (!formData.stage_description.trim()) {
      newErrors.stage_description = 'Stage description is required';
    }

    return newErrors;
  }, [formData]);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const { data, error } = await supabase
        .rpc('create_project_stage_with_team', {
          p_parent_project_id: parseInt(formData.parent_project_id),
          p_stage_id: formData.stage_id.trim(),
          p_stage_description: formData.stage_description.trim(),
          p_project_lead_id: formData.project_lead || null,
          p_team_member_ids: formData.team_members.length > 0 ? formData.team_members : null,
          p_primary_contact: formData.primary_contact ? parseInt(formData.primary_contact, 10) : null
        });

      if (error) throw error;

      if (data && !data.success) {
        throw new Error(data.error || 'Failed to create stage');
      }

      // Success - close modal and refresh data
      onStageCreated();
      onClose();
    } catch (err) {
      console.error('Error creating stage:', err);
      setErrors({
        // errorMessage, not instanceof Error: the RPC's own error is a
        // PostgrestError, a plain object whose message would be dropped.
        general: errorMessage(err, 'An error occurred while creating the stage')
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm, onStageCreated, onClose]);

  // Handle input changes
  const handleInputChange = useCallback((field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  // Toggle team member selection
  const handleToggleTeamMember = useCallback((memberId: string) => {
    setFormData(prev => {
      const isSelected = prev.team_members.includes(memberId);
      return {
        ...prev,
        team_members: isSelected
          ? prev.team_members.filter(id => id !== memberId)
          : [...prev.team_members, memberId]
      };
    });
  }, []);

  // Create dropdown items for project lead
  const projectLeadDropdownItems: DropdownItem[] = useMemo(() => [
    { id: '', label: 'Inherit from project', value: '' },
    ...teamMembers.map(member => ({
      id: member.id.toString(),
      label: `${member.first_name} ${member.last_name}`,
      value: member.id.toString()
    }))
  ], [teamMembers]);

  const stageIdTrimmed = formData.stage_id.trim();
  const numberingHint = parentJobNumber && stageIdTrimmed
    ? `Will be numbered ${parentJobNumber}.${stageIdTrimmed}`
    : 'The suffix on the job number: stage 1 of 12345 is 12345.1';

  // Modal footer
  const modalFooter = (
    <div className="pill-row">
      <button type="button" onClick={onClose} className="pill-btn pill-btn--ghost">
        Cancel
      </button>
      <button
        type="submit"
        form="add-stage-form"
        disabled={isSubmitting}
        className="pill-btn pill-btn--primary"
      >
        {isSubmitting ? 'Creating…' : 'Create stage'}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`New ${workflowType} stage`}
      size="sm"
      hasFooter={true}
      footerContent={modalFooter}
    >
      <div className="form-content add-stage-modal form-ui">
        <form id="add-stage-form" onSubmit={handleSubmit}>
          {errors.general && (
            <p className="field__error" role="alert">{errors.general}</p>
          )}

          <PanelHeading title="Stage" />
          <div className="pdm-grid pdm-grid--3">
            <Field label="Parent project" required className="span-all" error={errors.parent_project_id}>
              <SearchableProjectDropdown
                entityType="project"
                value={formData.parent_project_id || null}
                onChange={(projectId, project) => {
                  handleInputChange('parent_project_id', projectId ? String(projectId) : '');
                  setParentJobNumber(project?.job_number != null ? String(project.job_number) : null);
                  setParentClientId(project?.client_id ?? null);
                  // A contact chosen for the old parent belongs to its client.
                  handleInputChange('primary_contact', '');
                }}
                placeholder="Search by job number or description…"
              />
            </Field>
            <Field label="Stage ID" required htmlFor="stage_id" error={errors.stage_id} hint={numberingHint}>
              <TextInput
                id="stage_id"
                mono
                value={formData.stage_id}
                onChange={(e) => handleInputChange('stage_id', e.target.value)}
                invalid={!!errors.stage_id}
                placeholder="1"
              />
            </Field>
            <Field label="Description" required htmlFor="stage_description" className="span-2" error={errors.stage_description}>
              <TextInput
                id="stage_description"
                value={formData.stage_description}
                onChange={(e) => handleInputChange('stage_description', e.target.value)}
                invalid={!!errors.stage_description}
                placeholder="e.g. Stage 17, Civil"
              />
            </Field>
          </div>

          <PanelHeading spaced title="Contact" />
          <div className="pdm-grid pdm-grid--1">
            <StageContactField
              // Remount per parent: a contact form open for the old parent's
              // client would create its contact there.
              key={formData.parent_project_id || 'none'}
              value={formData.primary_contact}
              onChange={(contactId) => handleInputChange('primary_contact', contactId)}
              clientId={parentClientId}
              projectContactName={parentContactName}
              hasProject={!!formData.parent_project_id}
              hint={formData.parent_project_id
                ? "Leave as inherited to use the project's contact."
                : 'Choose the parent project first.'}
            />
          </div>

          <PanelHeading spaced title="Team" />
          <div className="pdm-grid pdm-grid--1">
            <Field label="Stage lead" hint="Leave as inherited to use the project's lead.">
              <Dropdown
                items={projectLeadDropdownItems}
                value={formData.project_lead}
                onChange={(value) => handleInputChange('project_lead', value)}
                placeholder="Inherit from project"
                searchable
              />
            </Field>
            <Field
              label="Team members"
              hint={formData.team_members.length > 0
                ? `${formData.team_members.length} ${formData.team_members.length === 1 ? 'member' : 'members'} selected`
                : 'Nobody selected yet'}
            >
              <MemberChips
                members={teamMembers}
                selected={formData.team_members}
                leadId={formData.project_lead || undefined}
                onToggle={handleToggleTeamMember}
              />
            </Field>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddStageModal;
